// @ts-nocheck
// Minimal create flow that replaces the retired registration wizard: pick a
// tournament or season, create the registration with sensible defaults, and
// land on the sectioned edit page to configure everything else.
//
// The registration is created INACTIVE — the admin activates it from the edit
// page's Overview section once it's configured.
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import _ from 'lodash';
import { useAuth } from '@/auth/clerkShim';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import useTournamentStore from '@store/tournamentStore';
import useLeagueStore from '@store/leagueStore';
import useRegistrationStore from '@store/registrationStore';
import helpers from '@/utils/helpers';
import { browserTimeZone } from '@/utils/registrationDateTz';
import { buildRegistrationSeed } from '@staty/shared/utils/registrationSeed';

export default function CreateRegistrationDialog({
  open,
  onOpenChange,
  organization,
  leagueName,
  initialType = 'tournament',
  initialParentId = '',
}) {
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const [flowType, setFlowType] = useState(initialType);
  const [parentId, setParentId] = useState(initialParentId);
  const [creating, setCreating] = useState(false);

  const { tournaments, getTournamentsByLeagueName } = useTournamentStore((state) => ({
    tournaments: state.tournaments,
    getTournamentsByLeagueName: state.getTournamentsByLeagueName,
  }));
  const { seasons, getSeasonsByLeagueName } = useLeagueStore((state) => ({
    seasons: state.leagues,
    getSeasonsByLeagueName: state.getLeaguesByOrganizationName,
  }));
  const { registrations, addRegistration } = useRegistrationStore((state) => ({
    registrations: state.registrations,
    addRegistration: state.addRegistration(getToken),
  }));

  useEffect(() => {
    if (!open || !leagueName) return;
    getTournamentsByLeagueName({ leagueName });
    getSeasonsByLeagueName(leagueName);
  }, [open, leagueName]);

  useEffect(() => {
    if (open) { setFlowType(initialType); setParentId(initialParentId); }
  }, [open, initialType, initialParentId]);

  const isTournament = flowType === 'tournament';
  const parents = isTournament ? tournaments : seasons;

  // A parent that already has a registration can't get a second one.
  const registeredParentIds = useMemo(() => {
    const ids = new Set();
    for (const r of registrations) {
      const t = helpers.getObjId(r.tournament);
      const c = helpers.getObjId(r.contest);
      if (t) ids.add(t);
      if (c) ids.add(c);
    }
    for (const p of parents) {
      if (p.registration) ids.add(helpers.getObjId(p._id));
    }
    return ids;
  }, [registrations, parents]);

  const onCreate = () => {
    const parent = _.find(parents, { _id: parentId });
    if (!parent || creating) return;
    setCreating(true);

    const sport = _.get(organization, 'sports.0', '');
    // ONE defaults factory for every registration-create surface (ADR 0037).
    // Seeding config from the parent's current values makes the API create's
    // parent-config write round-trip the same settings.
    const seed = buildRegistrationSeed({ sport, parentConfig: parent.config });
    const postData = {
      contestType: isTournament ? 'tournament' : 'season',
      ...(isTournament
        ? { tournament: parentId }
        : { contest: parentId, fieldId: _.snakeCase(parent.name), seasonName: parent.name }),
      organization: helpers.getObjId(organization?._id),
      league: helpers.getObjId(organization?._id),
      sports: sport ? [sport] : [],
      active: false,
      // (no collectPayments/collectDonations — not on RegistrationSchema, so they
      // never persisted. Attaching a paymentProduct is what enables a fee.)
      message: '',
      accessCode: '',
      timeZone: browserTimeZone(),
      ...seed,
    };

    addRegistration(postData, (r) => {
      setCreating(false);
      onOpenChange(false);
      navigate(`/admin/registration/${helpers.getObjId(r._id)}/edit`);
    });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!creating) onOpenChange(o); }}>
      <DialogContent data-tour="create-registration-dialog">
        <DialogHeader>
          <DialogTitle>Create Registration</DialogTitle>
          <DialogDescription>
            Choose what this registration is for. You&apos;ll configure payments, dates, and forms on
            the next page, then activate it when it&apos;s ready.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          <div className="space-y-2">
            <Label className="text-sm font-medium">Registration type</Label>
            <RadioGroup
              value={flowType}
              onValueChange={(v) => { setFlowType(v); setParentId(''); }}
              className="grid grid-cols-2 gap-3"
            >
              {[
                { value: 'tournament', label: 'Tournament', hint: 'A one-off event' },
                { value: 'league', label: 'Season', hint: 'An ongoing league season' },
              ].map((opt) => (
                <Label
                  key={opt.value}
                  htmlFor={`flow-${opt.value}`}
                  className={`flex cursor-pointer items-start gap-3 rounded-lg border-2 p-3 transition-colors ${
                    flowType === opt.value ? 'border-primary bg-primary/5' : 'border-muted hover:border-muted-foreground/30'
                  }`}
                >
                  <RadioGroupItem id={`flow-${opt.value}`} value={opt.value} className="mt-0.5" />
                  <span>
                    <span className="block text-sm font-semibold">{opt.label}</span>
                    <span className="block text-xs text-muted-foreground">{opt.hint}</span>
                  </span>
                </Label>
              ))}
            </RadioGroup>
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium">{isTournament ? 'Tournament' : 'Season'}</Label>
            <Select value={parentId} onValueChange={setParentId}>
              <SelectTrigger data-tour="create-registration-parent">
                <SelectValue placeholder={`Select a ${isTournament ? 'tournament' : 'season'}`} />
              </SelectTrigger>
              <SelectContent>
                {parents.length === 0 ? (
                  <div className="px-3 py-2 text-sm text-muted-foreground">
                    No {isTournament ? 'tournaments' : 'seasons'} yet. Create one in the Competitions tab first.
                  </div>
                ) : (
                  parents.map((p) => {
                    const taken = registeredParentIds.has(helpers.getObjId(p._id));
                    return (
                      <SelectItem key={p._id} value={p._id} disabled={taken}>
                        {p.name}{taken ? ' (already has a registration)' : ''}
                      </SelectItem>
                    );
                  })
                )}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={creating}>Cancel</Button>
          <Button onClick={onCreate} disabled={!parentId || creating} data-tour="create-registration-submit">
            {creating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Create & Configure
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
